/**
 * Fetch spoken audio from `elevenlabs-tts` and return an object URL for it.
 *
 * The function answers with raw `audio/mpeg`, which is the contract the iOS app
 * relies on; it is owned by the app repo and must not be re-added here. So read
 * the body as a Blob. `supabase.functions.invoke` is the wrong tool: for a
 * Content-Type it does not recognise it calls `response.text()`, which garbles
 * binary audio. useVoiceChat used invoke() and read a JSON `{success,
 * audioContent}` shape the function stopped returning in Feb 2026, so the
 * floating assistant never spoke while ElevenLabs still billed each call.
 *
 * The caller owns the URL and must `URL.revokeObjectURL` it when done.
 */
export async function fetchTtsAudioUrl(text: string, voiceId: string): Promise<string> {
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/elevenlabs-tts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: key, Authorization: `Bearer ${key}` },
    body: JSON.stringify({ text, voiceId }),
  });
  if (!res.ok) throw new Error(`TTS failed: ${res.status}`);
  return URL.createObjectURL(await res.blob());
}

/** Play TTS audio, calling `onDone` once when it ends, errors or fails to start. */
export async function playTts(
  text: string,
  voiceId: string,
  onDone: () => void,
  onAudio?: (audio: HTMLAudioElement) => void,
): Promise<void> {
  const url = await fetchTtsAudioUrl(text, voiceId);
  const audio = new Audio(url);
  onAudio?.(audio);
  const finish = () => { URL.revokeObjectURL(url); onDone(); };
  audio.onended = finish;
  audio.onerror = finish;
  try {
    await audio.play();
  } catch (e) {
    finish();
    throw e;
  }
}
